import { Controller } from '@hotwired/stimulus'
import { stimulus } from '~/init'

const NAME = 'lightbox-photo'
const DECODE_TIMEOUT = 800
const SLOP = 10
const DISTANCE = 0.25
const VELOCITY = 0.5

export default class Lightbox extends Controller {
  static targets = ['dialog', 'image', 'caption', 'previousNav', 'nextNav']

  connect () {
    this.onDown = (event) => this.#down(event)
    this.onMove = (event) => this.#move(event)
    this.onUp = (event) => this.#up(event)
    this.onCancel = () => { this.gesture = null }

    this.dialogTarget.addEventListener('pointerdown', this.onDown)
    this.dialogTarget.addEventListener('pointermove', this.onMove)
    this.dialogTarget.addEventListener('pointerup', this.onUp)
    this.dialogTarget.addEventListener('pointercancel', this.onCancel)
  }

  disconnect () {
    this.dialogTarget.removeEventListener('pointerdown', this.onDown)
    this.dialogTarget.removeEventListener('pointermove', this.onMove)
    this.dialogTarget.removeEventListener('pointerup', this.onUp)
    this.dialogTarget.removeEventListener('pointercancel', this.onCancel)
    this.gesture = null
    this.transition = null
    this.#unlock()
    this.#unmark()
  }

  #down (event) {
    if (event.pointerType === 'mouse' || !event.isPrimary || !this.dialogTarget.open) return
    if (event.target.closest('button')) return

    this.gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, axis: null, at: event.timeStamp }
  }

  #move (event) {
    const gesture = this.gesture
    if (!gesture || event.pointerId !== gesture.id || gesture.axis) return

    const dx = event.clientX - gesture.x
    const dy = event.clientY - gesture.y
    if (Math.hypot(dx, dy) < SLOP) return

    gesture.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y'
    if (gesture.axis === 'y') this.gesture = null
  }

  #up (event) {
    const gesture = this.gesture
    if (!gesture || event.pointerId !== gesture.id) return

    this.gesture = null
    if (gesture.axis !== 'x') return

    const dx = event.clientX - gesture.x
    const far = Math.abs(dx) > this.dialogTarget.clientWidth * DISTANCE
    const quick = Math.abs(dx) / Math.max(event.timeStamp - gesture.at, 1) > VELOCITY
    if (!far && !quick) return

    if (dx < 0) this.next()
    else this.previous()
  }

  // opening stays set until the dialog is actually open, so a double click opens it once.
  async open ({ params: { index } }) {
    const thumbnail = this.#thumbnail(index)
    if (!thumbnail || this.dialogTarget.open || this.opening) return

    this.opening = true
    this.closing = false
    this.index = index
    this.openedAt = index
    this.#render()
    await this.#show(thumbnail)
    if (!this.element.isConnected) {
      this.opening = false
      return
    }

    this.#transition(thumbnail, this.imageTarget, () => {
      this.#lock()
      this.dialogTarget.showModal()
      // showModal would otherwise land on the close button — the first control in the dialog —
      // and the first arrow key would light it up rather than anything being navigated.
      this.dialogTarget.focus({ preventScroll: true })
      this.opening = false
    })
  }

  close () {
    if (!this.dialogTarget.open || this.closing) return

    this.closing = true
    const thumbnail = this.#thumbnail(this.index)
    const shrinkOnto = this.index === this.openedAt ? thumbnail : null
    this.#transition(this.imageTarget, shrinkOnto, () => {
      this.dialogTarget.close()
      thumbnail?.closest('button').focus({ preventScroll: true })
    })
  }

  cancel (event) {
    event.preventDefault()
    this.close()
  }

  backdrop (event) {
    if (!event.target.closest('img, button')) this.close()
  }

  closed () {
    this.#unlock()
    this.closing = false
  }

  previous (event) {
    this.#quiet(event)
    return this.#go(this.index - 1)
  }

  next (event) {
    this.#quiet(event)
    return this.#go(this.index + 1)
  }

  #quiet (event) {
    if (event?.type === 'keydown') this.dialogTarget.focus({ preventScroll: true })
  }

  async #go (index) {
    if (!this.dialogTarget.open || this.closing || index < 0 || index >= this.#count) return

    const direction = index > this.index ? 'forward' : 'back'
    const thumbnail = this.#thumbnail(index)
    if (!thumbnail) return

    this.index = index
    this.#render()
    await this.#swap(thumbnail, direction)
  }

  get #count () {
    return this.element.querySelectorAll('[data-lightbox-index-param]').length
  }

  #render () {
    if (this.hasPreviousNavTarget) this.previousNavTarget.setAttribute('aria-disabled', this.index === 0)
    if (this.hasNextNavTarget) this.nextNavTarget.setAttribute('aria-disabled', this.index >= this.#count - 1)
  }

  #swap (thumbnail, direction) {
    if (!document.startViewTransition || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return this.#show(thumbnail, { wait: false })
    }

    document.documentElement.dataset.transition = `lightbox-${direction}`
    this.imageTarget.style.viewTransitionName = NAME
    const transition = this.#start(() => this.#show(thumbnail, { wait: false }))

    return transition.finished.catch(() => {}).finally(() => {
      if (!this.#finished(transition)) return

      this.#unname(this.imageTarget)
      this.#unmark()
    })
  }

  #thumbnail (index) {
    return this.element.querySelector(`[data-lightbox-index-param="${index}"] img[data-lightbox-src]`)
  }

  async #show (thumbnail, { wait = true } = {}) {
    const index = this.index
    const src = await this.#source(thumbnail, wait)
    if (index !== this.index) return

    this.imageTarget.src = src
    this.captionTarget.textContent = thumbnail.alt
    try { await this.imageTarget.decode() } catch {}
    this.#fit()
  }

  #fit () {
    const { naturalWidth: width, naturalHeight: height, style } = this.imageTarget
    if (!width || !height) return

    style.setProperty('--lightbox-ratio', width / height)
    style.setProperty('--lightbox-max', `${width}px`)
  }

  #source (thumbnail, wait) {
    const full = thumbnail.dataset.lightboxSrc
    const probe = new window.Image()
    probe.src = full
    const decoded = probe.decode()
    const copy = thumbnail.currentSrc || thumbnail.src

    decoded.then(() => {
      if (this.#thumbnail(this.index) === thumbnail) this.imageTarget.src = full
    }, () => {})

    if (!wait) return Promise.resolve(probe.complete && probe.naturalWidth ? full : copy)

    const timeout = new Promise((resolve, reject) => setTimeout(reject, DECODE_TIMEOUT))
    return Promise.race([decoded, timeout]).then(() => full, () => copy)
  }

  #transition (from, to, update) {
    if (!document.startViewTransition || !from || !to || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      update()
      return
    }

    document.documentElement.dataset.transition = 'lightbox'
    from.style.viewTransitionName = NAME
    const transition = this.#start(() => {
      this.#unname(from)
      to.style.viewTransitionName = NAME
      update()
    })
    transition.finished.catch(() => {}).finally(() => {
      if (!this.#finished(transition)) return

      this.#unname(from)
      this.#unname(to)
      this.#unmark()
    })
  }

  // Starting a transition while one is running skips the running one, and the skipped one settles
  // a microtask later — before the new one has captured anything. Its cleanup would then strip the
  // direction marker and the name the new one just wrote, which costs more than the lost slide:
  // motion.css keys the rules that hide every other view-transition-name off that same marker, so
  // the navbar logo and the article would paint over the viewer's backdrop. Only the transition
  // that is still the current one is allowed to clear up after itself.
  #start (callback) {
    this.transition = document.startViewTransition(callback)
    return this.transition
  }

  #finished (transition) {
    if (this.transition !== transition) return false

    this.transition = null
    return true
  }

  #unmark () {
    const root = document.documentElement
    if (root.dataset.transition?.startsWith('lightbox')) delete root.dataset.transition
  }

  #unname (element) {
    element.style.viewTransitionName = ''
    if (!element.style.length) element.removeAttribute('style')
  }

  #lock () {
    document.documentElement.classList.add('overflow-hidden')
  }

  #unlock () {
    document.documentElement.classList.remove('overflow-hidden')
  }
}

stimulus.register('lightbox', Lightbox)
